import { Box, Grid, useMediaQuery, useTheme } from "@mui/material"
import { memo, useMemo } from "react"

import TowerDevicePositionPlot from "widgets/plots/device/devicePosition/TowerDevicePositionPlot"
import type { TowerDevicePositionPlotValues } from "types/devicePosition.types"
import type { EntityType } from "types/common.types"
import type { RecordType } from "types/dashboard.types"

import {
  positionInTowerPlotDef,
  towerAmplitudeReqConfig,
  frontTiltStatsTableDef,
  rightTiltStatsTableDef,
  magnitudeTiltStatsTableDef,
  TOWER_TILT_FIELDS_INSTANT,
  MAGNITUDE_HEADING_FIELDS,
} from "helpers/config/dashboards/device"
import {
  TILT_FRONT_FIELD,
  TILT_RIGHT_FIELD,
  TILT_MAGNITUDE_FIELD,
  TILT_FRONT_SMOOTHED_FIELD,
  TILT_RIGHT_SMOOTHED_FIELD,
  TILT_MAGNITUDE_SMOOTHED_FIELD,
  FRONT_TILT_DRIFT_INSTANT_FIELD,
  FRONT_TILT_DRIFT_SMOOTHED_FIELD,
  RIGHT_TILT_DRIFT_INSTANT_FIELD,
  RIGHT_TILT_DRIFT_SMOOTHED_FIELD,
  MAGNITUDE_TILT_DRIFT_INSTANT_FIELD,
  MAGNITUDE_TILT_DRIFT_SMOOTHED_FIELD,
} from "helpers/config/telemetryFieldDefs"
import { GRID_LAYOUT_GAP, SM_ROW_HEIGHT, MD_ROW_HEIGHT } from "helpers/utils/constants"
import Plot from "widgets/plots/Plot"
import useDateRange from "helpers/hooks/useDateRange"
import { formatTelemetryData } from "widgets/plots/helpers"
import { useMultiDeviceTelemetry } from "helpers/hooks/plots/useMultiDeviceTelemetry"
import { getFieldDrift, getLatestAngles } from "helpers/utils/devicePosition"
import { useDashboardContext } from "helpers/hooks/useDashboardContext"
import { usePlotTabVisibility } from "helpers/hooks/plots/usePlotTabVisibility"
import PlotTabLegend from "widgets/plots/common/PlotTabLegend"

const PlotMemo = memo(Plot)

interface TowerDevicePositionProps {
  deviceId: number | string
  deviceIds: number[]
}

const TowerDevicePosition = ({ deviceId, deviceIds }: TowerDevicePositionProps) => {
  const { tabsConfig: tabsConfigDef, reqConfig } = positionInTowerPlotDef
  const tabsConfig = tabsConfigDef!
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.only("sm"))
  const { rowHeight: contextRowHeight } = useDashboardContext()
  const rowHeight = contextRowHeight ?? (isSmall ? SM_ROW_HEIGHT : MD_ROW_HEIGHT)

  const { getDateRangeTools } = useDateRange()
  const { dateRange, isDateRangeValid } = useMemo(
    () => getDateRangeTools("device"),
    [getDateRangeTools],
  )

  const tabVisibility = usePlotTabVisibility(tabsConfig)

  const visibleFields = useMemo(
    () => tabVisibility.visibleFields,
    [tabVisibility.visibleFields],
  )

  const {
    data,
    isFetching: isPositionLoading,
    qualityData,
    isRawData,
  } = useMultiDeviceTelemetry({
    deviceIds,
    reqConfig,
    dateRange,
    isDateRangeValid,
  })

  const { data: amplitudeData } = useMultiDeviceTelemetry({
    deviceIds,
    reqConfig: towerAmplitudeReqConfig,
    dateRange,
    isDateRangeValid,
  })

  const filteredPositionTelemetry = useMemo(
    () =>
      visibleFields
        ? data.filter((record) => !record._field || visibleFields.has(record._field))
        : data,
    [data, visibleFields],
  )

  const formattedData = useMemo(
    () => formatTelemetryData(filteredPositionTelemetry),
    [filteredPositionTelemetry],
  )

  // AngleView always shows the latest INSTANT values
  const latestInstantData = useMemo(
    () => getLatestAngles(data, TOWER_TILT_FIELDS_INSTANT),
    [data],
  )

  const latestMagnitudeHeading = useMemo(
    () => getLatestAngles(data, MAGNITUDE_HEADING_FIELDS),
    [data],
  )

  const positionPlotValues = useMemo<TowerDevicePositionPlotValues>(
    () => ({
      _time: latestInstantData._time,
      frontTilt: latestInstantData.first,
      rightTilt: latestInstantData.second,
      magnitude: latestMagnitudeHeading.first,
      heading: latestMagnitudeHeading.second,
    }),
    [latestInstantData, latestMagnitudeHeading],
  )

  // Drift computed client-side per field (last sample − first sample)
  const frontTiltDriftData = useMemo(
    () =>
      [
        {
          _field: FRONT_TILT_DRIFT_INSTANT_FIELD,
          _value: getFieldDrift(data, TILT_FRONT_FIELD),
        },
        {
          _field: FRONT_TILT_DRIFT_SMOOTHED_FIELD,
          _value: getFieldDrift(data, TILT_FRONT_SMOOTHED_FIELD),
        },
      ].filter((r) => r._value !== undefined),
    [data],
  )

  const rightTiltDriftData = useMemo(
    () =>
      [
        {
          _field: RIGHT_TILT_DRIFT_INSTANT_FIELD,
          _value: getFieldDrift(data, TILT_RIGHT_FIELD),
        },
        {
          _field: RIGHT_TILT_DRIFT_SMOOTHED_FIELD,
          _value: getFieldDrift(data, TILT_RIGHT_SMOOTHED_FIELD),
        },
      ].filter((r) => r._value !== undefined),
    [data],
  )

  const magnitudeTiltDriftData = useMemo(
    () =>
      [
        {
          _field: MAGNITUDE_TILT_DRIFT_INSTANT_FIELD,
          _value: getFieldDrift(data, TILT_MAGNITUDE_FIELD),
        },
        {
          _field: MAGNITUDE_TILT_DRIFT_SMOOTHED_FIELD,
          _value: getFieldDrift(data, TILT_MAGNITUDE_SMOOTHED_FIELD),
        },
      ].filter((r) => r._value !== undefined),
    [data],
  )

  // Each stats table renders amplitude (fetched) and drift (client-side) rows
  // together; the table-stat config keeps only rows whose field has data.
  const frontTiltTableData = useMemo<RecordType[]>(
    () => [...amplitudeData, ...frontTiltDriftData],
    [amplitudeData, frontTiltDriftData],
  )
  const rightTiltTableData = useMemo<RecordType[]>(
    () => [...amplitudeData, ...rightTiltDriftData],
    [amplitudeData, rightTiltDriftData],
  )
  const magnitudeTiltTableData = useMemo<RecordType[]>(
    () => [...amplitudeData, ...magnitudeTiltDriftData],
    [amplitudeData, magnitudeTiltDriftData],
  )

  const positionTabLegend = useMemo(
    () => <PlotTabLegend tabsConfig={tabsConfig} visibility={tabVisibility} />,
    [tabsConfig, tabVisibility],
  )

  const csvData = useMemo(
    (): {
      entity: EntityType
      entityIds: number[]
      fromDate: string
      toDate: string
    } => ({
      entity: "device",
      entityIds: [Number(deviceId)],
      fromDate: String(dateRange?.fromDate),
      toDate: String(dateRange?.toDate),
    }),
    [deviceId, dateRange?.fromDate, dateRange?.toDate],
  )

  if (!data.length) {
    return <></>
  }

  const gap = GRID_LAYOUT_GAP

  return (
    <>
      <Box height={"100%"} display={"flex"} flexDirection={"column"}>
        {Number.isFinite(latestInstantData._time) && (
          <TowerDevicePositionPlot
            values={positionPlotValues}
            isLoading={isPositionLoading}
          />
        )}
        <Grid container spacing={`${gap}px`} marginTop={`1px`}>
          {/* Tower position line chart */}
          <Grid item xs={12}>
            <Box height={rowHeight * 3}>
              <PlotMemo
                def={positionInTowerPlotDef}
                data={formattedData}
                csvData={csvData}
                entityData={{ entity: "device", ids: deviceIds }}
                qualityData={qualityData}
                isRawData={isRawData}
                seriesLegend={positionTabLegend}
              />
            </Box>
          </Grid>

          {/* Tilt stats tables: amplitude + drift, instant + smoothed */}
          <Grid container item xs={12} spacing={`${gap}px`}>
            <Grid item xs={12} md={4}>
              <Box height={rowHeight * 2.5}>
                <PlotMemo
                  def={frontTiltStatsTableDef}
                  data={frontTiltTableData}
                  entityData={{ entity: "device", ids: deviceIds }}
                  isRawData={isRawData}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box height={rowHeight * 2.5}>
                <PlotMemo
                  def={rightTiltStatsTableDef}
                  data={rightTiltTableData}
                  entityData={{ entity: "device", ids: deviceIds }}
                  isRawData={isRawData}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box height={rowHeight * 2.5}>
                <PlotMemo
                  def={magnitudeTiltStatsTableDef}
                  data={magnitudeTiltTableData}
                  entityData={{ entity: "device", ids: deviceIds }}
                  isRawData={isRawData}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default TowerDevicePosition
